/** Shared height/age recommendation engine. All garment ranges come from Liquid. */
export const EU_CODES = [92,98,104,110,116,122,128,134,140,146,152,158,164,170];
export function sizeKey(value) { return String(value).toLowerCase().split('/')[0].replace(/cm/g,'').trim(); }
export function recommend(data, height) {
  if (!data.series || !Number.isFinite(height)) return null;
  const offered = new Map((data.sizes || []).map(s => [sizeKey(s.value),s]));
  const rows = String(data.rows || '').split(';').filter(Boolean).map(text => {
    const [choices,bounds] = text.split('|');
    const [low,high] = bounds.split('–').map(Number);
    const keys = choices.split('/').map(sizeKey).filter(key => (!offered.size || offered.has(key)) && !(data.series === 'duang-tee' && key === 'l'));
    return {keys,low,high,distance:Math.max(low-height,height-high,0)};
  }).filter(row => row.keys.length && Number.isFinite(row.low) && Number.isFinite(row.high));
  if (!rows.length) return null;
  const distance = Math.min(...rows.map(row => row.distance));
  const keys = [...new Set(rows.filter(row => row.distance === distance).flatMap(row => row.keys))];
  return {sizes:keys.map(key => offered.get(key) || {value:key.toUpperCase(),label:key.toUpperCase()}), approximate:distance > 0,
    outside:height < Math.min(...rows.map(row=>row.low)) || height > Math.max(...rows.map(row=>row.high))};
}
export const normalizeAgeLabel = value => String(value).replace(/\b(110|120|130|140|150|160|170)(?:cm)?\s*\/\s*\d{1,2}\s*[-–]\s*\d{1,2}(?:\s*\p{L}+)?/giu,'$1');
const jsonIn = (root,selector) => JSON.parse(root.querySelector(selector)?.textContent || '{}');
const el = (tag,text,className) => {const node=document.createElement(tag);if(text!==undefined)node.textContent=text;if(className)node.className=className;return node;};
const localizedURL = path => new URL((window.Shopify?.routes?.root || document.documentElement.dataset.root || '/') + path,location.origin);
function previewURL(url) {
  const preview = new URL(location.href).searchParams.get('preview_theme_id');
  if (preview) url.searchParams.set('preview_theme_id',preview);
  return url;
}
let openDialog = null;
function finishClose(dialog) {
  if (!dialog?.open) return;
  dialog.close();document.documentElement.classList.remove('pb-size-modal-open');
  const opener=dialog._opener;
  if (dialog._keepVariant) {
    const url=new URL(location.href);url.searchParams.set('variant',dialog._keepVariant);
    history.replaceState(history.state,'',url);
  }
  if (opener?.isConnected) opener.focus({preventScroll:true});
  else document.querySelector(`[data-pb-size-open="${dialog.dataset.pbSizeDialog}"]`)?.focus({preventScroll:true});
  if (dialog._placeholder?.isConnected) dialog._placeholder.replaceWith(dialog);else dialog.remove();
  openDialog=null;
}
function closeGuide() {
  if (!openDialog) return;
  if (history.state?.pbSizeGuide) history.back();else finishClose(openDialog);
}
function openGuide(trigger) {
  const picker=trigger.closest('variant-picker');
  const dialog=picker?.querySelector('dialog[data-pb-size-dialog]');
  if (!dialog?.showModal) return false;
  if (openDialog) finishClose(openDialog);
  dialog._opener=trigger;dialog._picker=picker;dialog._pickerBlock=picker.dataset.blockId;
  dialog._keepVariant=null;dialog._placeholder=document.createComment('size guide');
  dialog.before(dialog._placeholder);document.body.append(dialog);
  openDialog=dialog;dialog.showModal();document.documentElement.classList.add('pb-size-modal-open');
  history.pushState({...history.state,pbSizeGuide:dialog.dataset.pbSizeDialog},'');
  dialog.querySelector('pb-size-tool')?.init();
  dialog.querySelector('[data-pb-size-close]')?.focus();
  return true;
}
class SizeTool extends (typeof HTMLElement === 'undefined' ? Object : HTMLElement) {
  connectedCallback() {queueMicrotask(()=>this.init());}
  init() {
    if(!this.querySelector('[data-pb-size-data]'))return;
    this.data=jsonIn(this,'[data-pb-size-data]');this.t=jsonIn(this,'[data-pb-size-i18n]');this.mode=this.mode || 'height';
    this.querySelector('[data-pb-size-query]')?.removeAttribute('hidden');
    this.setMode(this.mode);
    if(this._ready){this.update();return;}
    this._ready=true;
    this.addEventListener('click',event=>{
      const mode=event.target.closest('[data-pb-mode]');
      if(mode){this.setMode(mode.dataset.pbMode);this.update();}
      const choice=event.target.closest('[data-pb-choose]');
      if(choice&&!choice.disabled)this.choose(choice.dataset.pbChoose);
    });
    this.addEventListener('input',()=>this.update());this.addEventListener('change',()=>this.update());
    this.update();
  }
  setMode(mode) {
    if(!['height','eu','age'].includes(mode))return;
    this.mode=mode;
    this.querySelectorAll('[data-pb-mode]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.pbMode===mode)));
    this.querySelectorAll('[data-pb-input]').forEach(group=>group.hidden=group.dataset.pbInput!==mode);
    const hint=this.querySelector('[data-pb-query-hint]');
    if(hint)hint.textContent=this.t[mode==='height'?'query_hint':mode==='age'?'age_hint':'reference_hint'];
  }
  get query() {
    const input=this.querySelector(`[data-pb-${this.mode}]`);
    const value=input?.value || '';
    const number=value?Number(value):NaN;
    return {mode:this.mode,value,height:number>=50&&number<=200?number:NaN};
  }
  restore(mode,value) {
    this.init();this.setMode(mode);
    const input=this.querySelector(`[data-pb-${this.mode}]`);if(input)input.value=value || '';
    this.update();
  }
  picker() {
    const dialog=this.closest('dialog');
    const pickers=[...document.querySelectorAll(`variant-picker[data-product-id="${this.data.id}"]`)];
    return dialog?pickers.find(p=>p.dataset.blockId===dialog._pickerBlock)||dialog._picker:null;
  }
  selectedOptions() {
    const options=[...(this.data.selected || [])];
    const picker=this.picker();
    picker?.querySelectorAll('input:checked[data-pb-option-position],select[data-pb-option-position]').forEach(input=>options[Number(input.dataset.pbOptionPosition)-1]=input.value);
    return options;
  }
  variant(value) {
    const selected=this.selectedOptions();const sizeIndex=this.data.sizePosition-1;
    return (this.data.variants || []).find(variant=>variant.options.every((option,index)=>index===sizeIndex?option===value:(!selected[index]||option===selected[index])));
  }
  update() {
    if(!this._ready)return;
    const query=this.query, result=this.querySelector('[data-pb-result]');
    const rows=[...this.querySelectorAll('[data-eu-height]')];
    const nearest=Number.isFinite(query.height)?Math.min(...rows.map(row=>Math.abs(Number(row.dataset.euHeight)-query.height))):NaN;
    rows.forEach(row=>row.classList.toggle('pb-size-row--active',Math.abs(Number(row.dataset.euHeight)-query.height)===nearest));
    const guideLink=this.closest('dialog')?.querySelector('[data-pb-full-guide]');
    const active=(this.data.variants || []).find(variant=>variant.options.every((value,index)=>value===this.selectedOptions()[index]));
    if(guideLink){const url=previewURL(localizedURL('pages/size-guide'));url.searchParams.set('item',this.data.handle);if(active)url.searchParams.set('variant',active.id);if(query.value){url.searchParams.set('mode',this.mode);url.searchParams.set('value',query.value);}guideLink.href=url.href;}
    const returnLink=this.querySelector('[data-pb-return]');
    if(returnLink&&active){const url=previewURL(new URL(this.data.url,location.origin));url.searchParams.set('variant',active.id);returnLink.href=url.href;}
    if(!result)return;
    result.replaceChildren();result.hidden=!Number.isFinite(query.height);
    if(result.hidden)return;
    if(!this.data.handle) {
      const code=EU_CODES.reduce((best,code)=>Math.abs(code-query.height)<Math.abs(best-query.height)?code:best,EU_CODES[0]);
      result.append(el('strong',`EU ${code}`),el('p',this.t.choose_product));return;
    }
    const recommendation=recommend(this.data,query.height);
    if(!recommendation){result.append(el('p',this.t.independent));return;}
    result.append(el('p',recommendation.approximate?this.t.nearest:this.t.recommended,'pb-size-tool__result-label'));
    if(this.mode==='age')result.append(el('p',this.t.age_hint,'pb-size-tool__hint'));
    if(recommendation.outside)result.append(el('p',this.t.outside,'pb-size-tool__outside'));
    const choices=el('div',undefined,'pb-size-tool__choices');
    for(const size of recommendation.sizes) {
      const card=el('div',undefined,'pb-size-tool__choice');
      card.append(el('strong',size.label || normalizeAgeLabel(size.value)));
      const measurement=this.data.measurements;
      if(measurement?.values?.[sizeKey(size.value)]!==undefined) {
        const number=new Intl.NumberFormat(document.documentElement.lang || 'en').format(measurement.values[sizeKey(size.value)]);
        card.append(el('span',`${this.t[measurement.label] || this.t.length}: ${number} cm`));
      }
      const variant=this.variant(size.value);
      const button=el('button',variant?.available?this.t.choose:this.t.sold_out);
      button.type='button';button.dataset.pbChoose=size.value;button.disabled=!variant?.available;
      button.setAttribute('aria-label',`${variant?.available?this.t.choose:this.t.sold_out}: ${size.label}`);
      card.append(button);choices.append(card);
    }
    result.append(choices);
  }
  choose(value) {
    const variant=this.variant(value);if(!variant?.available)return;
    const picker=this.picker();
    if(picker) {
      const input=[...picker.querySelectorAll('input[type="radio"]')].find(input=>input.value===value&&Number(input.dataset.pbOptionPosition)===this.data.sizePosition);
      const select=[...picker.querySelectorAll('select')].find(input=>Number(input.dataset.pbOptionPosition)===this.data.sizePosition);
      if(input)input.click();else if(select){select.value=value;select.dispatchEvent(new Event('change',{bubbles:true}));}else return;
      const dialog=this.closest('dialog');if(dialog&&picker.dataset.templateProductMatch==='true'&&!picker.closest('dialog'))dialog._keepVariant=variant.id;
      closeGuide();
    }else {const url=previewURL(new URL(this.data.url,location.origin));url.searchParams.set('variant',variant.id);location.assign(url.href);}
  }
}
class SizePage extends (typeof HTMLElement === 'undefined' ? Object : HTMLElement) {
  connectedCallback(){queueMicrotask(()=>this.init());}
  init() {
    if(this._ready)return;this._ready=true;
    this.t=jsonIn(this,'[data-pb-size-i18n]');this.search=this.querySelector('[data-pb-product-search]');
    this.results=this.querySelector('[data-pb-search-results]');this.status=this.querySelector('[data-pb-search-status]');
    this.querySelector('[data-pb-search-ui]')?.removeAttribute('hidden');
    this.search?.addEventListener('input',()=>{clearTimeout(this._timer);this._searchAbort?.abort();this._timer=setTimeout(()=>this.searchItems(),250);});
    this.addEventListener('click',event=>{const item=event.target.closest('[data-pb-size-item]');if(item)this.loadItem(item.dataset.pbSizeItem);});
    this.search?.addEventListener('keydown',event=>{
      if(event.key==='ArrowDown'){event.preventDefault();this.results.querySelector('button')?.focus();}
      if(event.key==='Escape'){this.results.replaceChildren();this.status.textContent='';}
    });
    this.results?.addEventListener('keydown',event=>{
      const buttons=[...this.results.querySelectorAll('button')],index=buttons.indexOf(document.activeElement);
      if(event.key==='Escape'){this.search.focus();this.results.replaceChildren();}
      if(event.key==='ArrowDown'||event.key==='ArrowUp'){event.preventDefault();buttons[(index+(event.key==='ArrowDown'?1:-1)+buttons.length)%buttons.length]?.focus();}
    });
    const params=new URL(location.href).searchParams;
    if(params.get('item'))this.loadItem(params.get('item'),{mode:params.get('mode'),value:params.get('value'),variant:params.get('variant')});
    else this.querySelector('pb-size-tool')?.restore(params.get('mode') || 'height',params.get('value'));
  }
  async searchItems() {
    const query=this.search.value.trim();this.results.replaceChildren();if(query.length<2){this.status.textContent='';return;}
    this._searchAbort=new AbortController();this.status.textContent=this.t.loading;
    const url=previewURL(localizedURL('search/suggest'));url.searchParams.set('q',query);url.searchParams.set('section_id','pb-size-search');
    url.searchParams.set('resources[type]','product');url.searchParams.set('resources[limit]','10');
    try {
      const response=await fetch(url,{signal:this._searchAbort.signal});if(!response.ok)throw new Error('search');
      const doc=new DOMParser().parseFromString(await response.text(),'text/html'),list=doc.querySelector('.pb-size-search__results');
      if(!list)throw new Error('missing section');
      this.results.replaceChildren(list);this.status.textContent=list.querySelector('button')?'':this.t.search_empty;
    }catch(error){if(error.name!=='AbortError')this.status.textContent=this.t.search_error;}
  }
  async loadItem(handle,query) {
    if(!/^[a-z0-9-]+$/.test(handle || ''))return;
    this._itemAbort?.abort();this._itemAbort=new AbortController();
    query=query || this.querySelector('pb-size-tool')?.query;
    this.status.textContent=this.t.loading;
    try {
      const url=previewURL(localizedURL(`products/${handle}`));url.searchParams.set('section_id','pb-size-guide-data');
      if(/^\d+$/.test(query?.variant || ''))url.searchParams.set('variant',query.variant);
      const response=await fetch(url,{signal:this._itemAbort.signal});if(!response.ok)throw new Error('item');
      const doc=new DOMParser().parseFromString(await response.text(),'text/html'),tool=doc.querySelector('pb-size-tool');
      if(!tool || jsonIn(tool,'[data-pb-size-data]').handle!==handle)throw new Error('wrong product');
      const target=this.querySelector('[data-pb-matched]');target.replaceChildren(tool);tool.restore(query?.mode || 'height',query?.value);
      this.querySelector('[data-pb-overview]').hidden=true;this.results.replaceChildren();this.status.textContent='';this.search.value=tool.querySelector('.pb-size-tool__item')?.textContent || handle;
      const state=new URL(location.href);state.searchParams.set('item',handle);history.replaceState(history.state,'',state);
    }catch(error){if(error.name!=='AbortError'){
      const link=el('a',this.t.return_product,'pb-size-tool__link');
      link.href=previewURL(localizedURL(`products/${handle}`)).href;
      this.status.replaceChildren(el('span',this.t.search_error),link);
    }}
  }
}
if(typeof document!=='undefined') {
  document.documentElement.classList.add('pb-size-enabled');
  if(!customElements.get('pb-size-tool'))customElements.define('pb-size-tool',SizeTool);
  if(!customElements.get('pb-size-page'))customElements.define('pb-size-page',SizePage);
  document.addEventListener('click',event=>{
    const trigger=event.target.closest('[data-pb-size-open]');
    if(trigger&&openGuide(trigger))event.preventDefault();
    if(event.target.closest('[data-pb-size-close]'))closeGuide();
  });
  document.addEventListener('cancel',event=>{if(event.target===openDialog){event.preventDefault();closeGuide();}},true);
  window.addEventListener('popstate',()=>{if(openDialog)finishClose(openDialog);});
  document.addEventListener('change',()=>openDialog?.querySelector('pb-size-tool')?.update());
  const normalizeSticky=()=>document.querySelectorAll('.sticky-add-to-cart__variant,.pb-sticky-variant-label').forEach(node=>{const text=normalizeAgeLabel(node.textContent);if(text!==node.textContent)node.textContent=text;});
  normalizeSticky();new MutationObserver(normalizeSticky).observe(document.body,{childList:true,subtree:true,characterData:true});
}
